/**
 * Cookie-based auth only. No token storage in JS.
 * Authentication is via HttpOnly cookie (MHA_JWT_AUTH) - tokens are never exposed to JavaScript.
 * Auth state is managed in Redux and initialized from GET /api/account/current-token.
 */
import {
  storageAuthRefreshToken,
  storageRoles,
  storageAuthInformation,
  paymentsUser,
  appealsUser,
  claimsUser,
  admin,
  readOnly,
  canImpersonate,
  canAddEditUsers,
  clientPortal_claimsUser,
  clientPortal_employer,
  clientPortal_healthSystem,
  XSRFToken,
  reportsUser,
} from '../constants/strings';
import { FeatureTypeEnum } from '../enums/featureTypeEnum';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface AuthDto {
  status: AuthStatus;  // Tracks initialization state: 'unknown' (not initialized), 'authenticated', 'anonymous'
  isAuthenticated: boolean;
  isAdmin: boolean;
  isPasswordExpired: boolean;
  authToken: string;
  refreshToken: string;
  roles: string[];
  fullName: string;
  employerId: string;
  contactId: string;
  employers: string[];
}

const roles = (): string[] =>
  JSON.parse(sessionStorage.getItem(storageRoles) ?? JSON.stringify([]));
export const authInfo = (): AuthDto =>
  JSON.parse(
    sessionStorage.getItem(storageAuthInformation) ?? JSON.stringify(authInit)
  ) as AuthDto;
const isReadOnlyByFeature = (
  feature: number,
  auto: boolean = false
): boolean => {
  switch (feature) {
    case FeatureTypeEnum.Employers:
      if (isAppealsUser()) return true;
      break;
    case FeatureTypeEnum.Settings:
      if (isClaimsUser()) return true;
      if (isPaymentsUser()) return true;
      break;
    case FeatureTypeEnum.Contacts:
      return false;
    default:
      return auto;
  }

  return auto;
};

export const authInit: AuthDto = {
  status: 'unknown',  // Initial state: not yet initialized from cookie
  isAuthenticated: false,
  isAdmin: false,
  isPasswordExpired: false,
  authToken: '',
  refreshToken: '',
  roles: [],
  fullName: '',
  employerId: '',
  contactId: '',
  employers: [],
};
/**
 * Checks if user is authenticated by checking Redux auth state.
 * Cookie-based auth only. No token storage in JS.
 * 
 * IMPORTANT: This function requires Redux state to be initialized.
 * Call initializeAuthActions.request() on app load to hydrate auth state from HttpOnly cookie.
 * 
 * @param authState - Redux auth state (from state.auth)
 * @returns true if user is authenticated and auth state is initialized
 */
export const isUserAuthenticated = (authState?: AuthDto): boolean => {
  // If authState is provided, check Redux state directly
  if (authState) {
    return authState.isAuthenticated === true && 
           Array.isArray(authState.roles) && 
           authState.roles.length > 0;
  }
  
  // Fallback: check sessionStorage metadata (for backward compatibility during transition)
  // This should only be used if Redux state is not yet initialized
  const roles = sessionStorage.getItem(storageRoles);
  if (roles) {
    try {
      const rolesArray = JSON.parse(roles);
      return Array.isArray(rolesArray) && rolesArray.length > 0;
    } catch {
      return false;
    }
  }
  
  return false;
};

export const isPasswordExpired = (): boolean =>
  authInfo().isPasswordExpired ?? authInit.isPasswordExpired;

/**
 * @deprecated Cookie-based auth only. No token storage in JS.
 * This function always returns empty string - authentication is via HttpOnly cookie only.
 * Do not use this function - it exists only for backward compatibility during transition.
 */
export const getToken = (): string => '';
export const getXSRFToken = (): string =>
  sessionStorage.getItem(XSRFToken) ?? 'test';
export const getRefreshToken = (): string =>
  sessionStorage.getItem(storageAuthRefreshToken) ?? '';
export const getFullName = (): string => authInfo().fullName;
export const getEmployerId = (): string => authInfo().employerId;
export const getContactId = (): string => authInfo().contactId;
export const getEmployers = (): string[] => authInfo().employers;

export const isAdmin = (): boolean =>
  roles().findIndex((item: string) => item === admin) > -1;
export const isClientPortal_ClaimsUser = (): boolean =>
  roles().findIndex((item: string) => item === clientPortal_claimsUser) > -1;
export const isClientPortal_Employer = (): boolean =>
  roles().findIndex((item: string) => item === clientPortal_employer) > -1;
export const isClientPortal_HealthSystem = (): boolean =>
  roles().findIndex((item: string) => item === clientPortal_healthSystem) > -1;
export const isClientPortalUser = (): boolean => {
  if (
    isAdmin() !== true &&
    roles().findIndex(
      (item: string) =>
        item === clientPortal_employer || item === clientPortal_healthSystem
    ) > -1
  )
    return true;
  else return false;
};

export const isAppealsUser = (): boolean =>
  roles().findIndex((item: string) => item === appealsUser) > -1;
export const isClaimsUser = (): boolean =>
  roles().findIndex((item: string) => item === claimsUser) > -1;
export const isPaymentsUser = (): boolean =>
  roles().findIndex((item: string) => item === paymentsUser) > -1;
export const canUserImpersonate = (): boolean =>
  roles().findIndex((item: string) => item === canImpersonate) > -1;
export const canSetupUsers = (): boolean =>
  roles().findIndex(
    (item: string) => item === canAddEditUsers || item === admin
  ) > -1;
export const isReadOnly = (feature: number): boolean => {
  if (isAdmin()) return false;
  if (roles().findIndex((item: string) => item === readOnly) > -1)
    return isReadOnlyByFeature(feature, true);

  return isReadOnlyByFeature(feature);
};

export const isAuthorized = (): boolean => {
  if (
    roles().findIndex(
      (item: string) =>
        item === admin ||
        item === appealsUser ||
        item === claimsUser ||
        item === paymentsUser ||
        item === readOnly
    ) > -1
  )
    return true;
  else return false;
};

export const canAccessActionList = (): boolean =>
  isUserAuthenticated() && !isReadOnly(0);
export const canAccessPayments = (): boolean =>
  roles().findIndex((item: string) => item === admin || item === paymentsUser) >
  -1;
export const canAccessEmployers = (): boolean => isAuthorized();
export const canAccessContacts = (): boolean => isAuthorized();
export const canAccessClaims = (): boolean => isUserAuthenticated();
export const canAccessReports = (): boolean =>
  roles().findIndex((item: string) => item === admin || item === reportsUser) >
  -1;
export const canAccessSettings = (): boolean =>
  roles().findIndex((item: string) => item === admin || item === claimsUser) >
  -1;
export const canAccessHelp = (): boolean =>
  isUserAuthenticated() && !isAuthorized();
